import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLanguage } from "../i18n/LanguageContext";
import {
  getProjects,
  getMyProjects,
  getProject,
  createProject,
  updateProject,
  patchProject,
  updateProjectStatus,
  submitProject,
  deleteProject,
  getProjectBrief,
  getProjectStatuses,
  getProjectArchiveInfo,
  ProjectArchiveInfo,
} from "../api/projects-api";
import {
  Project,
  ProjectCreateRequest,
  ProjectUpdateRequest,
  ProjectStatusUpdateRequest,
} from "../models/Project";
import { ProjectBrief } from "../models/ProjectBrief";
import { PaginatedResponse } from "../models/Pagination";
import { JsonPatchOperation } from "../utils/jsonPatchUtils";

/**
 * Hook to get paginated list of projects (for CSM/Admin)
 */
export const useProjects = (
  page = 1, 
  pageSize = 10, 
  status?: string,
  name?: string,
  projectNumber?: string,
  companyId?: number,
  sortField?: string,
  sortDirection?: string
) => {
  const { currentLanguage } = useLanguage();

  return useQuery<PaginatedResponse<Project>, Error>({
    queryKey: ["projects", page, pageSize, status, name, projectNumber, companyId, sortField, sortDirection, currentLanguage],
    queryFn: () => getProjects(page, pageSize, status, name, projectNumber, companyId, sortField, sortDirection),
  });
};

/**
 * Hook to get paginated list of user's projects (for company users).
 *
 * The optional `enabled` flag lets callers defer the fetch when the surrounding
 * state isn't ready yet — e.g. ClientRoutes probes for "has any project?" only
 * once the onboarding completion flag is known, so it doesn't fire the query
 * for clients still in the onboarding wizard.
 */
export const useMyProjects = (
  page = 1,
  pageSize = 10,
  status?: string,
  name?: string,
  projectNumber?: string,
  companyId?: number,
  sortField?: string,
  sortDirection?: string,
  enabled = true
) => {
  const { currentLanguage } = useLanguage();

  return useQuery<PaginatedResponse<Project>, Error>({
    queryKey: ["myProjects", page, pageSize, status, name, projectNumber, companyId, sortField, sortDirection, currentLanguage],
    queryFn: () => getMyProjects(page, pageSize, status, name, projectNumber, companyId, sortField, sortDirection),
    enabled,
  });
};

/**
 * Hook to get a specific project by ID
 */
export const useProject = (id: number) => {
  const { currentLanguage } = useLanguage();

  return useQuery<Project, Error>({
    queryKey: ["project", id, currentLanguage],
    queryFn: () => getProject(id),
    enabled: id !== 0,
  });
};

/**
 * Hook to create a new project
 */
export const useCreateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (projectData: ProjectCreateRequest) => createProject(projectData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["myProjects"] });
    },
  });
};

/**
 * Hook to update a project
 */
export const useUpdateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ProjectUpdateRequest }) =>
      updateProject(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["project", id] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["myProjects"] });
    },
  });
};

/**
 * Hook to patch a project using JSON Patch operations
 */
export const usePatchProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, operations }: { id: number; operations: JsonPatchOperation[] }) =>
      patchProject(id, operations),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["project", id] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["myProjects"] });
    },
  });
};

/**
 * Hook to update project status
 */
export const useUpdateProjectStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, statusUpdate }: { id: number; statusUpdate: ProjectStatusUpdateRequest }) =>
      updateProjectStatus(id, statusUpdate),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["project", id] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["myProjects"] });
    },
  });
};

/**
 * Hook to submit a project
 */
export const useSubmitProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => submitProject(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["project", id] });
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["myProjects"] });
    },
  });
};



/**
 * Hook to delete a project
 */
export const useDeleteProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      queryClient.invalidateQueries({ queryKey: ["myProjects"] });
    },
  });
};

/**
 * Hook to get a project brief by project ID
 */
export const useProjectBrief = (projectId: number) => {
  const { currentLanguage } = useLanguage();

  return useQuery<ProjectBrief, Error>({
    queryKey: ["projectBrief", projectId, currentLanguage],
    queryFn: () => getProjectBrief(projectId),
    enabled: projectId !== 0,
  });
};

/**
 * Hook to get all available project statuses
 */
export const useProjectStatuses = () => {
  return useQuery<string[], Error>({
    queryKey: ["projectStatuses"],
    queryFn: () => getProjectStatuses(),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour since statuses rarely change
  });
};

/**
 * Hook to get archive information for a project (who archived it and when)
 */
export const useProjectArchiveInfo = (projectId: number, isArchived: boolean) => {
  return useQuery<ProjectArchiveInfo, Error>({
    queryKey: ["projectArchiveInfo", projectId],
    queryFn: () => getProjectArchiveInfo(projectId),
    enabled: projectId !== 0 && isArchived,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour since archive info doesn't change
  });
};